import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { can as canFn, type MeResponse, type ModuleKey } from '@tww/shared';
import { api, ApiRequestError } from '../api/client';

interface AuthCtx {
  me: MeResponse | null;
  loading: boolean;
  can: (module: ModuleKey, level?: 'read' | 'write') => boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}
const Ctx = createContext<AuthCtx>({ me: null, loading: true, can: () => false, refresh: async () => {}, logout: async () => {} });

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.get<MeResponse>('/auth/me');
      } catch (e) {
        if (e instanceof ApiRequestError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
  useEffect(() => {
    const h = () => qc.setQueryData(['me'], null);
    window.addEventListener('tww:unauthorized', h);
    return () => window.removeEventListener('tww:unauthorized', h);
  }, [qc]);
  const me = q.data ?? null;
  const can = useCallback((module: ModuleKey, level: 'read' | 'write' = 'read') => canFn(me?.permissions, module, level), [me]);
  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ['me'] });
  }, [qc]);
  const logout = useCallback(async () => {
    const r = await api.post<{ ok: boolean; redirect: string | null }>('/auth/logout');
    qc.clear();
    if (r.redirect) window.location.href = r.redirect;
    else window.location.href = '/login';
  }, [qc]);
  const value = useMemo(() => ({ me, loading: q.isLoading, can, refresh, logout }), [me, q.isLoading, can, refresh, logout]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
export const useAuth = () => useContext(Ctx);
export const useCan = () => useAuth().can;
